import {useModal} from '@hooks';
import {useTranslation} from 'react-i18next';
import {ModalCell} from '@molecules';
import {DeskSelectionModalPage, MapDetailsModalPage, MapPreviewWithSuggestionsCard} from '@organisms';
import {
  MeetingInviteeSearchResult,
  getAreaById,
  getFloorHasMap,
  getDesksByAreaId,
  getSelectedNodes,
  clearDeskSuggestions,
  clearSuggestionError,
  useAppDispatch,
  useAppSelector,
} from '@lib/store';
import {useEffect, useState} from 'react';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {LocalTimespan, OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';

export function WorkspaceOnMapSection({
  invitees,
  date,
  workday,
  workspaceReservation,
  timespan,
}: {
  timespan: LocalTimespan;
  invitees: MeetingInviteeSearchResult[];
  date: string;
  workday: OfficeWorkday;
  workspaceReservation?: WorkspaceReservation;
}) {
  const {addPages} = useModal();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const {buildingId, floorId, areaId, deskIds} = useAppSelector(getSelectedNodes);

  const reservationId = workspaceReservation?.id;

  // All desks in the area with their names and whether they're enabled (static)
  const floorHasMap = useAppSelector(getFloorHasMap(floorId));
  const areaNode = useAppSelector((state) => getAreaById(state, areaId));
  const deskBookingRequired = Boolean(areaNode?.hasBookableDesks);
  const areaDesks = useAppSelector((state) => getDesksByAreaId(state, areaId));
  const groupSize = invitees.length + 1;

  // Trick the component to not update when reservationId is dynamically loaded
  // Note: this means you can never have this component in a piece of UI that
  //       should survive switching between create & edit mode!
  const [initialReservationId] = useState(reservationId);

  useEffect(
    function autoLoadDeskSuggestions() {
      dispatch(clearDeskSuggestions());
      dispatch(clearSuggestionError());

      // We need an area before we can suggest desks
      if (!areaId) return;

      // Only fetch suggestions for areas with desk booking
      if (!deskBookingRequired) return;

      // Note: it is unclear what the expected behavior is for editing group desk bookings.
      //       Desks reserverd for invitees will be exlcuded from suggestions because we're not
      //       explicitly telling the suggestion logic those are owned by us.
      //       That means you can assign other desks to your invitees, but the ones you already
      //       selected will be disabled.
      const currentlyBookedDeskIds =
        workspaceReservation?.workspace.nodeType === 'Desk' ? [workspaceReservation.workspace.deskId] : [];

      dispatch(
        actions.loadDeskSuggestionsAndData({
          buildingId: workday.buildingId,
          areaId,
          startDate: timespan.localStart,
          endDate: timespan.localEnd,
          currentlyBookedDeskIds,
          deskCount: groupSize,
        }),
      );
    },
    [
      areaId,
      deskBookingRequired,
      date,
      groupSize,
      initialReservationId,
      dispatch,
      workday.buildingId,
      timespan.localStart,
      timespan.localEnd,
    ],
  );

  // Desk names are shown when users are tapped
  const deskNames = (deskIds ?? []).flatMap((id) => {
    const desk = areaDesks.find((d) => d.id === id);

    return desk && desk.isEnabled ? [desk.name] : [];
  });

  if (!buildingId || !floorId || !areaId || !floorHasMap) return <></>;

  return (
    <ModalCell>
      <MapPreviewWithSuggestionsCard
        buildingId={buildingId}
        floorId={floorId}
        areaId={areaId}
        deskIds={deskIds}
        groupSize={groupSize}
        deskNames={deskNames}
        buttonLabel={deskBookingRequired ? t('Change') : t('View')}
        onClick={() => {
          addPages([
            deskBookingRequired ? (
              <DeskSelectionModalPage
                date={date}
                invitees={invitees}
                workday={workday}
                workspaceReservation={workspaceReservation}
                timespan={timespan}
              />
            ) : (
              <MapDetailsModalPage
                date={date}
                areaId={areaId}
                buildingId={buildingId}
                floorId={floorId}
                groupSize={groupSize}
                timespan={timespan}
              />
            ),
          ]);
        }}
      />
    </ModalCell>
  );
}
